/**
 * TOSS Web Application — the account block at the foot of the side navigation
 *
 * Who is signed in, and the menu that leads to their own screen or out of the product. One shape
 * for both audiences since 2026-08-26; what differs is what is IN the menu, and that arrives as a
 * render prop — the internal one logs out through `logoutWithPushCleanup()` (FCM tokens the portal
 * never registers) and links to an internal profile route `InternalOnlyRoute` refuses a customer.
 *
 * **It opens on click, not on hover** (owner's choice of the cabinet's behaviour). The internal
 * block opened its flyout on `mouseenter` with a 120 ms delay: it fired while the pointer was on
 * its way to something else, and on a tablet — where this rail is permanently collapsed — there is
 * no hover at all, so the block had no way to open.
 *
 * **Through `DropdownPortal`, not `absolute`.** `sidebar-transition` puts `contain: layout style`
 * on the `<aside>`, which makes it a containing block and a stacking context; a menu anchored
 * inside it reads as clipped on the 64 px rail. `openUpwards` exists for this call site: the
 * default computed `top = rect.bottom + offset` and never flipped, so a menu opened from the FOOT
 * of the sidebar started below the viewport.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';
import { DropdownPortal, UserAvatar } from '@/components/ui';
import { Tooltip } from './Tooltip';

// ============================================================================
// Constants
// ============================================================================

/** One row of the account menu. Exported so both surfaces' items look alike. */
export const sidebarAccountItemClass = `
  flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left
  text-[var(--color-text-primary)]
  hover:bg-[var(--color-bg-secondary)]
  disabled:opacity-50 disabled:cursor-not-allowed
  transition-colors
`;

// ============================================================================
// Types
// ============================================================================

export interface SidebarAccountProps {
  fullName: string;
  photoUrl?: string | null;
  /** Second line of the collapsed tooltip: the company for a customer, the email for staff. */
  secondaryLabel?: string;
  collapsed: boolean;
  /** Tint the trigger while the account's own screen is open. */
  isProfileActive?: boolean;
  /** The menu's rows. `close` is handed in so a row can dismiss the menu after acting. */
  children: (close: () => void) => ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function SidebarAccount({
  fullName,
  photoUrl,
  secondaryLabel,
  collapsed,
  isProfileActive = false,
  children,
}: SidebarAccountProps): JSX.Element {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // A menu that outlives the navigation it started would hang over the screen it opened.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const close = (): void => setIsOpen(false);

  const menu = (
    <DropdownPortal
      isOpen={isOpen}
      onClose={close}
      triggerRef={triggerRef}
      placement="bottom-start"
      openUpwards
      className={`
        ${collapsed ? 'min-w-[200px]' : 'min-w-[224px]'}
        overflow-hidden rounded-[var(--radius-lg)]
        border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-[var(--shadow-overlay)]
      `}
    >
      {children(close)}
    </DropdownPortal>
  );

  const avatar = <UserAvatar photoUrl={photoUrl ?? undefined} fullName={fullName} size="sm" showBorder />;
  const triggerLabel = `${fullName} — открыть меню учётной записи`;
  const tint = isOpen || isProfileActive ? 'bg-[var(--color-bg-secondary)]' : '';

  if (collapsed) {
    return (
      <div
        className="px-2 pt-3 pb-3 mt-1 border-t border-[var(--color-border-light)] flex flex-col items-center"
      >
        {menu}
        {/* Silent while the menu is open: the tooltip anchors to the right of the avatar, which is
            where the menu's lower half sits on a 64 px rail — and it repeats a name the menu is
            already about. */}
        <Tooltip
          content={secondaryLabel ? `${fullName} · ${secondaryLabel}` : fullName}
          side="right"
          disabled={isOpen}
        >
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label={triggerLabel}
            className={`flex items-center justify-center p-1 rounded-lg transition-colors hover:bg-[var(--color-bg-secondary)] ${tint}`}
          >
            {avatar}
          </button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="px-3 pt-3 pb-3 mt-1 border-t border-[var(--color-border)]" >
      {menu}

      {/* The whole row is the trigger — the name is what a person aims at. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={triggerLabel}
        className={`flex w-full items-center gap-3 min-w-0 px-2 py-1.5 rounded-lg transition-colors hover:bg-[var(--color-bg-secondary)] ${tint}`}
      >
        {avatar}
        <span
          className="flex-1 min-w-0 text-sm font-semibold text-[var(--color-text-primary)] truncate text-left"
          title={fullName}
        >
          {fullName}
        </span>
        <ChevronUp
          className={`w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)] transition-transform ${
            isOpen ? '' : 'rotate-180'
          }`}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

export default SidebarAccount;
